import type { FC } from 'react';
import { useState } from 'react';

import { useCoreDispatch } from '@pp/core/src/store/CoreStore';
import {
  AutoplayControlsContainer,
  AutoplayControlsRow,
} from '@pp/core/src/components/game/autoplayV2/controls/AutoplayControlsLayout';
import { CrashAutoplayModifyButtonConnected } from './autoplayButtons/CrashAutoplayModifyButtonConnected';
import { CrashAutoplayStopButtonConnected } from './autoplayButtons/CrashAutoplayStopButtonConnected';
import { Tooltip } from '@pp/core/src/components/tooltip/Tooltip';
import { Bets } from '../../../config/bets';
import { useCrashAutoplaySlice } from '../../../store/slices/crashAutoplay/selectors';
import { crashAutoplayActions } from '../../../store/slices/crashAutoplay/slice';
import { Logger } from '@pp/core/src/logger/Logger';
import { toastActions } from '@pp/core/src/store/slices/toast/slice';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { useHighflyerStateSlice } from '../../../store/slices/highflyerState/selectors';
import { useOnCancelCallbackConnected } from '../../gameGrid/hooks/useOnCancelCallbackConnected';

export const CrashAutoplayActiveControlsConnected: FC<{ betId: Bets }> = ({ betId }) => {
  const dispatch = useCoreDispatch();
  const roundsLeft = useCrashAutoplaySlice.getRemainingCount(betId);
  const isInProgress = useCrashAutoplaySlice.isInProgress(betId);
  const maxRoundsLimit = useCrashAutoplaySlice.getAutoplayMaxRoundsLimit(betId);
  const isRoundsLimitReached = maxRoundsLimit === roundsLeft;
  const isBetsOpen = useGameStateSlice.isBetsOpenStatus();
  const isInDealingInProgress = useHighflyerStateSlice.isDealingInProgress(betId);
  const roundsAddons = useCrashAutoplaySlice.getAutoplayRoundsAddons(betId);
  const onCancel = useOnCancelCallbackConnected(betId);

  // On stopping autoplay, the controls should be disabled while following the animation
  const [disableControls, setDisableControls] = useState(false);

  const handleStop = (): void => {
    if (!isBetsOpen && isInDealingInProgress) onCancel(betId);
    setDisableControls(true);
    dispatch(toastActions.createToast('AUTOPLAY_STOPPED'));
    dispatch(crashAutoplayActions.stop({ betsSpot: betId }));
    Logger.logEvent('click', 'autoplay_stop', Date.now(), 1, 'Manual', false);
  };

  return (
    <AutoplayControlsContainer hidden={!isInProgress}>
      <AutoplayControlsRow>
        <CrashAutoplayStopButtonConnected betId={betId} onClick={handleStop} disabled={disableControls} />
      </AutoplayControlsRow>
      <Tooltip
        disabled={!isRoundsLimitReached}
        displayOnTouch
        text="AUTOBET_MAXIMUM_ROUND_LIMIT"
        attrs={[String(maxRoundsLimit)]}
      >
        <AutoplayControlsRow>
          {roundsAddons.map((addOn) => (
            <CrashAutoplayModifyButtonConnected betId={betId} addOn={addOn} key={addOn} disabled={disableControls} />
          ))}
        </AutoplayControlsRow>
      </Tooltip>
    </AutoplayControlsContainer>
  );
};
